/**
 * RouteStats Component
 * 
 * Displays summary statistics for operator's route
 */

import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type RouteStats as RouteStatsType } from '@/types/store-first';
import { CheckCircle2, Clock, MapPin, PlayCircle, ShieldCheck } from 'lucide-react';

interface RouteStatsProps {
  stats: RouteStatsType;
  isSignedOff?: boolean;
  signedAt?: string;
}

export const RouteStats = memo(function RouteStats({ stats, isSignedOff, signedAt }: RouteStatsProps) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Route Overview
          {isSignedOff && (
            <span className="ml-auto flex items-center gap-1 text-sm font-normal text-green-600">
              <ShieldCheck className="h-4 w-4" />
              Signed Off
            </span>
          )}
        </CardTitle>
        {isSignedOff && signedAt && (
          <p className="text-xs text-gray-500 mt-1">
            Signed off on {new Date(signedAt).toLocaleDateString()}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <div className="text-2xl font-bold text-blue-600">{stats.totalStores}</div>
            <div className="text-sm text-gray-600">Total Stores</div>
          </div>
          
          <div className="flex flex-col">
            <div className="text-2xl font-bold text-green-600">{stats.completedStores}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Completed
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="text-2xl font-bold text-orange-600">{stats.inProgressStores}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <PlayCircle className="h-3 w-3" />
              In Progress
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="text-2xl font-bold text-gray-600">{stats.pausedStores}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Paused
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>
              {stats.totalStores > 0 
                ? Math.round((stats.completedStores / stats.totalStores) * 100) 
                : 0}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${stats.totalStores > 0 
                  ? (stats.completedStores / stats.totalStores) * 100 
                  : 0}%`
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

